import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import PageLayout from 'layouts/PageLayout';
import Dialog from 'shared/components/Dialog';
import sleep from 'shared/utils/sleep';

import { chatApi } from '../api/chatApi';
import getNotReadChatMessagesIds from '../lib/getNotReadMessagesIds';
import useUpdateNotificationQuery from '../lib/useUpdateNotificationQuery';
import { EntityName } from '../model/enums';
import { TChatMessage } from '../model/types';

import { ChatContainer, ChatLoader } from './styled';
import ChatForm from './ChatForm';
import ChatMessagesList from './ChatMessagesList';
import ChatMessage from './ChatMessage';
import DeleteChatMessageConfirmationDialogContent from './DeleteChatMessageConfirmationDialogContent';

const DELAY_BEFORE_READING = 4000;
const EMPTY_CHAT_MESSAGES_LIST: TChatMessage[] = [];

type ChatProps = {
  entityId: string;
  entityName: EntityName;

  /**
   * @default true
   */
  canBeModified?: boolean;
  displayNotifyPMOnlyCheckbox?: boolean;
  displayShowToContactPersonCheckbox?: boolean;
  pollingInterval?: number;
  readonly?: boolean;
};

const Chat = ({
  entityId,
  entityName,

  canBeModified = true,
  displayNotifyPMOnlyCheckbox = false,
  displayShowToContactPersonCheckbox = false,
  pollingInterval = 4000,
  readonly = false,
}: ChatProps) => {
  const isPollingActive = useRef(true);

  const { chatMessages, isLoading, isError } = chatApi.useGetChatMessagesQuery(
    {
      entityId,
      entityName,
    },
    {
      refetchOnMountOrArgChange: true,
      refetchOnReconnect: true,
      pollingInterval,
      skip: !isPollingActive.current,
      selectFromResult: ({ data, ...rest }) => ({
        ...rest,
        chatMessages: data ?? EMPTY_CHAT_MESSAGES_LIST,
      }),
    }
  );
  const [markAsRead] = chatApi.useMarkAsReadMutation();
  const [deleteChatMessage] = chatApi.useDeleteMessageMutation();
  const updateNotification = useUpdateNotificationQuery({ entityName });

  const [activeChatMessage, setActiveChatMessage] =
    useState<TChatMessage | null>(null);
  const [chatMessageRequestedForDeletion, setChatMessageRequestedForDeletion] =
    useState<TChatMessage | null>(null);

  const showChatForm = !readonly;
  const openDeleteChatMessageConfirmationDialog =
    !!chatMessageRequestedForDeletion;

  const notReadMessagesIds = useMemo(
    () => getNotReadChatMessagesIds(chatMessages),
    [chatMessages]
  );

  // TODO: Handle error
  const readChatMessages = useCallback(
    async (ids: string[]) => {
      try {
        await sleep(DELAY_BEFORE_READING);
        await markAsRead(ids).unwrap();
        updateNotification();
      } catch {}
    },
    [markAsRead, updateNotification]
  );

  const handleDeleteChatMessage = useCallback(() => {
    if (!chatMessageRequestedForDeletion) return;

    deleteChatMessage({
      chatMessageId: chatMessageRequestedForDeletion.Id,
      entityId,
      entityName,
    });
  }, [chatMessageRequestedForDeletion, entityId, entityName]);

  const handleOpenDeleteChatMessageConfirmationDialog = (
    chatMessage: TChatMessage
  ) => {
    setChatMessageRequestedForDeletion(chatMessage);
  };

  const handleCloseDeleteChatMessageConfirmationDialog = () => {
    setChatMessageRequestedForDeletion(null);
  };

  const handleSetActiveChatMessage = useCallback(
    (chatMessage: TChatMessage) => {
      setActiveChatMessage(chatMessage);
    },
    []
  );

  const handleResetActiveChatMessage = useCallback(() => {
    setActiveChatMessage(null);
  }, []);

  useEffect(() => {
    if (!notReadMessagesIds.length) return;

    readChatMessages(notReadMessagesIds);
  }, [notReadMessagesIds, readChatMessages]);

  useEffect(() => {
    if (isError) isPollingActive.current = false;
  }, [isError]);

  if (isLoading) return <ChatLoader />;

  return (
    <PageLayout>
      <ChatContainer>
        <ChatMessagesList chatMessages={chatMessages}>
          {({ chatMessage, ref }) => (
            <ChatMessage
              canBeModified={canBeModified}
              chatMessage={chatMessage}
              isActive={chatMessage.Id === activeChatMessage?.Id}
              ref={ref}
              onClickDeleteButton={
                readonly
                  ? undefined
                  : handleOpenDeleteChatMessageConfirmationDialog
              }
              onClickEditButton={
                readonly ? undefined : handleSetActiveChatMessage
              }
            />
          )}
        </ChatMessagesList>
        {showChatForm && (
          <ChatForm
            activeChatMessage={activeChatMessage}
            displayNotifyPMOnlyCheckbox={displayNotifyPMOnlyCheckbox}
            displayShowToContactPersonCheckbox={
              displayShowToContactPersonCheckbox
            }
            entityId={entityId}
            entityName={entityName}
            onResetActiveChatMessage={handleResetActiveChatMessage}
          />
        )}
      </ChatContainer>
      <Dialog
        fullWidth
        maxWidth="xs"
        open={openDeleteChatMessageConfirmationDialog}
        onClose={handleCloseDeleteChatMessageConfirmationDialog}
      >
        <DeleteChatMessageConfirmationDialogContent
          onClose={handleCloseDeleteChatMessageConfirmationDialog}
          onDelete={handleDeleteChatMessage}
        />
      </Dialog>
    </PageLayout>
  );
};

export default Chat;
